import { ActionContext, ActionTree } from "vuex";
import GroupsService from "../services/groups.service";
import { IGroupsState } from "./state";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import type {
  GroupBookingApiDetail,
  GroupBookingDetail,
  GroupBookingPassengerUpdate,
  GroupBookingRequest,
  GroupPaymentOtpRequest,
  GroupPayNowRequest,
  GroupsListData,
} from "../types";
import { mapGroup } from "../utils/groupMapper";

const groupsService = new GroupsService();

const unwrapGroups = (payload: unknown): GroupsListData["groups"] => {
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const record = payload as Record<string, unknown>;
  if (Array.isArray(record.groups)) {
    return record.groups as GroupsListData["groups"];
  }

  const nested = record.data as Record<string, unknown> | undefined;
  if (nested && Array.isArray(nested.groups)) {
    return nested.groups as GroupsListData["groups"];
  }

  return [];
};

const mapBookingDetail = (payload: unknown): GroupBookingDetail | null => {
  const detail = (payload as { data?: GroupBookingApiDetail })?.data;
  if (!detail?.public_id) return null;
  return {
    ...detail,
    invoices: detail.invoices ?? [],
    passengers: detail.passengers ?? [],
    group: mapGroup(detail.group),
  };
};

const actions: ActionTree<IGroupsState, StoreState> = {
  async fetchGroups(context: ActionContext<IGroupsState, StoreState>) {
    try {
      context.commit("setGroupsLoading", true);

      const response = await groupsService.fetchGroups();

      if (response.success) {
        context.commit("setGroups", unwrapGroups(response.data).map(mapGroup));
        return response;
      }

      throw response;
    } catch (error) {
      const errorMessage =
        (error as { message?: string })?.message || "Unable to fetch groups";
      context.commit("setGroups", []);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = errorMessage;
      notificationService.triggerNotification();
      return error;
    } finally {
      context.commit("setGroupsLoading", false);
    }
  },

  async bookGroup(
    context: ActionContext<IGroupsState, StoreState>,
    payload: GroupBookingRequest,
  ) {
    try {
      context.commit("setGroupBookingLoading", true);

      const response = await groupsService.bookGroup(payload);

      if (response.success) {
        const detail = mapBookingDetail(response.data);
        context.commit("setGroupBookingDetail", detail);
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "Group booking submitted.";
        notificationService.triggerNotification();
        return detail?.public_id || null;
      }

      throw response;
    } catch (error) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        (error as { message?: string })?.message ||
        "Unable to submit group booking";
      notificationService.triggerNotification();
      return null;
    } finally {
      context.commit("setGroupBookingLoading", false);
    }
  },

  async fetchGroupBookingDetail(
    context: ActionContext<IGroupsState, StoreState>,
    bookingId: string,
  ) {
    try {
      context.commit("setGroupBookingDetailLoading", true);

      const response = await groupsService.fetchBookingDetail(bookingId);

      if (response.success) {
        context.commit(
          "setGroupBookingDetail",
          mapBookingDetail(response.data),
        );
        return;
      }

      throw response;
    } catch (error) {
      context.commit("setGroupBookingDetail", null);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        (error as { message?: string })?.message ||
        "Unable to fetch booking details";
      notificationService.triggerNotification();
    } finally {
      context.commit("setGroupBookingDetailLoading", false);
    }
  },

  async updateGroupBookingPassengers(
    context: ActionContext<IGroupsState, StoreState>,
    {
      bookingId,
      passengers,
    }: { bookingId: string; passengers: GroupBookingPassengerUpdate[] },
  ) {
    try {
      context.commit("setGroupPassengersUpdating", true);

      const response = await groupsService.updateBookingPassengers(
        bookingId,
        passengers,
      );

      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "Passengers updated successfully.";
        notificationService.triggerNotification();
        return true;
      }

      throw response;
    } catch (error) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        (error as { message?: string })?.message ||
        "Unable to update passengers";
      notificationService.triggerNotification();
      return false;
    } finally {
      context.commit("setGroupPassengersUpdating", false);
    }
  },

  async generateGroupPaymentOtp(
    context: ActionContext<IGroupsState, StoreState>,
    payload: GroupPaymentOtpRequest,
  ): Promise<string | null> {
    try {
      context.commit("setGroupPayingInvoiceId", payload.payment_id);

      const response = await groupsService.generatePaymentOtp(payload);
      const otpReferenceId: string | undefined =
        response.data?.data?.otp_reference_id;

      if (response.success && otpReferenceId) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "OTP generated successfully.";
        notificationService.triggerNotification();
        return otpReferenceId;
      }

      throw response;
    } catch (error) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        (error as { message?: string })?.message || "Unable to generate OTP";
      notificationService.triggerNotification();
      return null;
    } finally {
      context.commit("setGroupPayingInvoiceId", null);
    }
  },

  async payGroupBookingInvoice(
    context: ActionContext<IGroupsState, StoreState>,
    payload: GroupPayNowRequest,
  ) {
    try {
      context.commit("setGroupPayingInvoiceId", payload.payment_id);

      const response = await groupsService.payNow(payload);

      if (response.success) {
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || "Payment completed successfully.";
        notificationService.triggerNotification();
        await context.dispatch("fetchGroupBookingDetail", payload.booking_id);
        return true;
      }

      throw response;
    } catch (error) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        (error as { message?: string })?.message ||
        "Unable to complete payment";
      notificationService.triggerNotification();
      return false;
    } finally {
      context.commit("setGroupPayingInvoiceId", null);
    }
  },
};

export default actions;
